import type { ReactNode } from "react";
import { Link } from "wouter";
import { Logo } from "../lib/brand";
import { useI18n } from "../lib/i18n";
import "../pages/Signup.css";

// Signup and activity setup share decoration/layout only. Desktop can host choices
// beside the form; compact screens reserve the single form column for each step.
export function SignupLayout({ desktopActivities, plainMobile = false, children }: { desktopActivities?: ReactNode; plainMobile?: boolean; children: ReactNode }) {
  const { t } = useI18n();

  return (
    <div className={`signup-page ${plainMobile ? "signup-plain-mobile" : ""} relative isolate flex min-h-[100dvh] flex-col overflow-x-clip bg-[#f5f7fb] xl:bg-navy`}>
      {plainMobile && <div className="signup-tablet-background pointer-events-none absolute inset-0 -z-10 hidden" aria-hidden="true">
        <img src="/login-cityscape.webp" alt="" width="1536" height="1024" className="h-full w-full object-cover object-bottom opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/70 via-transparent to-paper/30" />
      </div>}
      <div className="pointer-events-none absolute inset-0 -z-10 hidden xl:block" aria-hidden="true">
        <img src="/signup-navy-buildings.webp" alt="" width="1536" height="1024" className="h-full w-full object-fill" />
      </div>
      <header className="signup-header container-x w-full bg-navy py-5 sm:py-6 xl:bg-transparent">
        {plainMobile ? <><div className="xl:hidden"><Logo /></div><div className="hidden xl:block"><Logo light /></div></> : <Logo light />}
      </header>
      <div className="signup-content grid w-full flex-1 content-start items-start xl:container-x xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] xl:content-normal xl:gap-16 xl:pt-6 xl:pb-10">
        <section aria-labelledby="signup-introduction" className="signup-activities hidden xl:block xl:pt-6">
          <div className="mx-auto w-full max-w-[480px] xl:mx-0">
            <h2 id="signup-introduction" className="text-[36px] font-bold leading-[1.15] tracking-tight text-white">What would you like to do on KontrataPH?</h2>
            <p className="mt-4 max-w-[440px] text-sm leading-6 text-white/80">
              One account, multiple activities. Pick any or all — you can switch anytime, and you'll never be locked into a single role.
            </p>
            {desktopActivities && <div className="mt-8 hidden xl:block"><h2 className="mb-4 text-base font-semibold text-white">Choose your activities</h2>{desktopActivities}</div>}
          </div>
        </section>
        <div className="signup-wave pointer-events-none xl:hidden" aria-hidden="true">
          <svg className="block h-full w-full" viewBox="0 0 1440 80" preserveAspectRatio="none">
            <rect width="1440" height="80" fill="#0f2540" />
            <path d="M0 22C240 50 420 8 720 28C1000 54 1220 12 1440 24V80H0Z" fill="#345f89" />
            <path d="M0 32C240 60 420 18 720 38C1000 64 1220 22 1440 34V80H0Z" fill="#9dbbdd" />
            <path d="M0 42C240 70 420 28 720 48C1000 74 1220 32 1440 44V80H0Z" fill="#f5f7fb" />
          </svg>
        </div>
        <section aria-labelledby="signup-form-heading" className="signup-form px-4 pt-5 pb-8 sm:px-6 sm:pt-6 sm:pb-10 xl:px-0 xl:py-10">
          <div className="signup-form-panel mx-auto w-full max-w-[440px] xl:mr-0">{children}</div>
        </section>
      </div>
      {plainMobile && <footer className="signup-tablet-footer relative mx-auto hidden shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-xs leading-5 text-mut">
        <span>© {new Date().getFullYear()} KontrataPH</span><span aria-hidden="true">·</span><Link href="/legal/privacy" className="hover:text-navy hover:underline">{t("legal.privacy")}</Link>
      </footer>}
    </div>
  );
}
